import React, { useState, useEffect } from 'react';
import { useAsyncAction } from '../../hooks/useAsyncAction';

export default function AdminAccountsPage() {
  const [stats, setStats] = useState(null);
  const [email, setEmail] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const { loading: looking, error: lookupError, run: runLookup } = useAsyncAction();

  // Novel list for manual unlock dropdown
  const [novels, setNovels] = useState([]);
  const [unlockNovelId, setUnlockNovelId] = useState('');
  const { loading: unlocking, error: unlockError, run: runUnlock } = useAsyncAction();
  const [unlockMsg, setUnlockMsg] = useState('');

  useEffect(() => {
    fetch('/api/admin/accounts', { credentials: 'include' })
      .then(r => r.json()).then(setStats);
    fetch('/api/admin/novels', { credentials: 'include' })
      .then(r => r.json()).then(d => setNovels(Array.isArray(d) ? d : []));
  }, []);

  async function handleLookup(e) {
    e.preventDefault();
    setLookupResult(null); setUnlockMsg('');
    await runLookup(async () => {
      const res = await fetch(`/api/admin/accounts/lookup?email=${encodeURIComponent(email)}`,
        { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Not found');
      setLookupResult(data);
    });
  }

  async function handleUnlock() {
    if (!unlockNovelId || !lookupResult) return;
    setUnlockMsg('');
    await runUnlock(async () => {
      const res = await fetch(`/api/admin/accounts/${lookupResult.id}/unlock-novel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ novel_id: unlockNovelId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setUnlockMsg('Novel unlocked successfully.');
      // Refresh lookup to show updated unlock list
      const fresh = await fetch(`/api/admin/accounts/lookup?email=${encodeURIComponent(email)}`,
        { credentials: 'include' });
      if (fresh.ok) setLookupResult(await fresh.json());
    });
  }

  return (
    <>
      <h1 style={s.h1}>Accounts</h1>

      {/* ── Stats ── */}
      {stats && (
        <div style={s.statsGrid}>
          {[
            { label: 'Total Teachers', value: stats.total_teachers },
            { label: 'TpT Lightweight', value: stats.tpt_lightweight },
            { label: 'Full Accounts', value: stats.full_accounts },
            { label: 'Active Subscriptions', value: stats.active_subscriptions },
            { label: 'Expiring in 30 Days', value: stats.expiring_soon },
          ].map(({ label, value }) => (
            <div key={label} style={s.statCard}>
              <div style={s.statNum}>{value ?? '—'}</div>
              <div style={s.statLabel}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Lookup ── */}
      <div style={s.section}>
        <h2 style={s.h2}>Account Lookup</h2>
        <form onSubmit={handleLookup} style={s.lookupRow}>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="teacher@example.com" required style={s.input} />
          <button type="submit" disabled={looking} style={s.lookupBtn}>
            {looking ? 'Looking up…' : 'Look up'}
          </button>
        </form>

        {lookupError && <p style={s.error}>{lookupError}</p>}

        {lookupResult && (
          <div style={s.resultCard}>
            <div style={s.resultGrid}>
              <div><span style={s.fieldLabel}>Email</span><br />{lookupResult.email || '—'}</div>
              <div><span style={s.fieldLabel}>Classroom Name</span><br />{lookupResult.classroom_name}</div>
              <div><span style={s.fieldLabel}>Account Type</span><br />{lookupResult.account_type}</div>
              <div>
                <span style={s.fieldLabel}>Subscription</span><br />
                {lookupResult.sub_status || 'None'}
                {lookupResult.sub_expires_at
                  ? ` · expires ${new Date(lookupResult.sub_expires_at).toLocaleDateString()}`
                  : ''}
              </div>
              <div><span style={s.fieldLabel}>Member Since</span><br />{new Date(lookupResult.created_at).toLocaleDateString()}</div>
            </div>

            <div style={{ marginTop: '16px' }}>
              <span style={s.fieldLabel}>TpT Unlocked Novels</span>
              {lookupResult.tpt_novels?.length > 0 ? (
                <ul style={s.novelList}>
                  {lookupResult.tpt_novels.map(n => <li key={n.novel_id}>{n.title}</li>)}
                </ul>
              ) : <p style={s.muted}>None</p>}
            </div>

            {/* Manual unlock */}
            <div style={{ marginTop: '16px' }}>
              <span style={s.fieldLabel}>Manual Novel Unlock</span>
              <div style={{ ...s.lookupRow, marginTop: '6px' }}>
                <select value={unlockNovelId} onChange={e => setUnlockNovelId(e.target.value)} style={s.select}>
                  <option value="">Select a novel…</option>
                  {novels.map(n => <option key={n.id} value={n.id}>{n.title}</option>)}
                </select>
                <button onClick={handleUnlock} disabled={!unlockNovelId || unlocking} style={s.unlockBtn}>
                  {unlocking ? 'Unlocking…' : 'Unlock'}
                </button>
              </div>
              {unlockError && <p style={s.error}>{unlockError}</p>}
              {unlockMsg && <p style={s.ok}>{unlockMsg}</p>}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const s = {
  h1: { fontSize: '22px', fontWeight: '700', color: '#111827', margin: '0 0 24px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '12px', marginBottom: '28px' },
  statCard: { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
    padding: '16px', textAlign: 'center' },
  statNum: { fontSize: '28px', fontWeight: '700', color: '#1a1a2e' },
  statLabel: { fontSize: '12px', color: '#6b7280', marginTop: '4px' },
  section: { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '24px' },
  h2: { fontSize: '16px', fontWeight: '600', color: '#374151', margin: '0 0 14px' },
  lookupRow: { display: 'flex', gap: '8px', alignItems: 'center' },
  input: { flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px',
    fontSize: '14px', maxWidth: '320px' },
  lookupBtn: { padding: '9px 18px', backgroundColor: '#1a1a2e', color: '#fff', border: 'none',
    borderRadius: '6px', fontWeight: '600', fontSize: '14px', cursor: 'pointer' },
  error: { color: '#dc2626', fontSize: '13px', margin: '8px 0 0' },
  resultCard: { marginTop: '16px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb',
    borderRadius: '8px', padding: '20px' },
  resultGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '12px', fontSize: '14px', color: '#374151' },
  fieldLabel: { fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase',
    letterSpacing: '0.05em' },
  novelList: { margin: '6px 0 0', paddingLeft: '20px', fontSize: '14px', color: '#374151' },
  muted: { color: '#6b7280', fontSize: '13px', margin: '4px 0 0', fontStyle: 'italic' },
  select: { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px',
    fontSize: '14px', minWidth: '200px' },
  unlockBtn: { padding: '8px 16px', backgroundColor: '#16a34a', color: '#fff', border: 'none',
    borderRadius: '6px', fontWeight: '600', fontSize: '14px', cursor: 'pointer' },
  ok: { color: '#16a34a', fontSize: '13px', margin: '6px 0 0', fontWeight: '600' },
};
